import { useCallback, useEffect, useState } from "react";
import { theme, card, emptyFrame, btnPrimary, inputStyle, type as text } from "../theme";
import { StatusTag, type StatusTone } from "./Drawing";
import { api, apiErrorMessage } from "../api/client";
import { fmtDate } from "../lib/format";
import { fmtDateOnly } from "../lib/dateOnly";
import { safeHref } from "../utils/safeHref";
import { SeverityPill, secondaryBtn, dangerBtn } from "../pages/resilience/shared";
import { SupplierAssuranceReports } from "./SupplierAssuranceReports";

// The supplier panel's Assurance tab (docs/TPRM-SUPPLIER-ASSURANCE.md): what the platform can see
// about a supplier from the outside and from its own records, read together as signals —
//   * the standing and its signals (lib/supplierSignals.ts) — signals, never a score;
//   * the supplier's assurance reports, read and confirmed item by item (SupplierAssuranceReports);
//   * passive checks of the supplier's public domain (DNS and its website only: nothing of the
//     supplier's is tested);
//   * the vendor rating, where the organisation connects its own ratings account;
//   * the supplier's guest accounts in the organisation's Microsoft 365 tenant;
//   * its own suppliers (fourth parties): recorded, reported, published or inferred from DNS.
// GET /tprm/suppliers/:id/assurance is the one read; every write is tprm:write on the server.

// ── Types (mirror the GET /tprm/suppliers/:id/assurance response) ────────────────

export type SupplierStanding = "act" | "watch" | "steady" | "unknown";
type SignalState = "good" | "concern" | "serious" | "unknown";
type DomainCheckKey = "spf" | "dmarc" | "tls" | "hsts" | "security_txt";
type CheckState = "pass" | "warn" | "fail" | "unknown";

interface Signal { key: string; state: SignalState; detail: string; source: string }

interface DomainCheck {
  checkedAt: string;
  result: {
    domain: string;
    host: string;
    checks: Record<DomainCheckKey, { state: CheckState; summary: string }>;
    findings: Array<{ check: string; severity: "high" | "medium" | "low"; title: string; detail: string }>;
    certificateExpiresAt: string | null;
  };
  /** False when the check was of a domain the supplier no longer has (absent on older APIs). */
  current?: boolean;
}

interface Rating { observedOn: string; score: number; scaleMax: number; grade: string | null; vendor: string }

interface GuestAccount {
  id: string;
  displayName: string | null;
  userPrincipalName: string;
  accountEnabled: boolean;
  lastSignInAt: string | null;
  recentSignIns: number;
  recentMfaSignIns: number;
  flags: string[];
}

export interface FourthParty {
  id: string;
  name: string;
  service: string | null;
  country: string | null;
  lei: string | null;
  handlesPersonalData: boolean | null;
  sources: string[];
  inferredFrom: string | null;
  confirmedAt: string | null;
  firstSeenAt: string | null;
  lastSeenAt: string | null;
  removedAt: string | null;
  notes: string | null;
  linkedSupplier: { id: string; name: string } | null;
}

interface AssuranceView {
  supplierId: string;
  domain: { domain: string; from: "set" | "website" | "contact" } | null;
  settings: { domain: string | null; subProcessorListUrl: string | null };
  signals: Signal[];
  standing: SupplierStanding;
  domainCheck: DomainCheck | null;
  domainHistory: Array<{ checkedAt: string; findingCount: number; worstSeverity: string | null }>;
  ratings: Rating[];
  guests: GuestAccount[];
  fourthParties: FourthParty[];
  subProcessorWatch: { url: string | null; checkedAt: string | null; changedAt: string | null; error: string | null } | null;
}

// ── Labels ────────────────────────────────────────────────────────────────────

export const STANDING_LABELS: Record<SupplierStanding, string> = {
  act: "Needs action", watch: "Watch", steady: "Steady", unknown: "Not enough known",
};
const STANDING_TONE: Record<SupplierStanding, StatusTone> = { act: "gap", watch: "partial", steady: "met", unknown: "none" };

const SIGNAL_LABELS: Record<string, string> = {
  questionnaire: "Latest questionnaire",
  review: "Periodic review",
  certifications: "Certifications",
  assurance_report: "Assurance report",
  contracts: "Agreements",
  incidents: "Incidents involving the supplier",
  domain: "Public domain checks",
  rating: "Vendor rating",
  access: "Guest access to your tenant",
  fourth_parties: "Its own suppliers",
};
const SIGNAL_TONE: Record<SignalState, StatusTone> = { serious: "gap", concern: "partial", good: "met", unknown: "none" };
const SIGNAL_STATE_LABELS: Record<SignalState, string> = { serious: "Serious", concern: "Concern", good: "Good", unknown: "Unknown" };
const SIGNAL_RANK: Record<SignalState, number> = { serious: 0, concern: 1, good: 2, unknown: 3 };

const DOMAIN_CHECKS: Array<{ key: DomainCheckKey; label: string }> = [
  { key: "spf", label: "SPF" },
  { key: "dmarc", label: "DMARC" },
  { key: "tls", label: "Website TLS" },
  { key: "hsts", label: "HSTS" },
  { key: "security_txt", label: "security.txt" },
];
const CHECK_TONE: Record<CheckState, StatusTone> = { pass: "met", warn: "partial", fail: "gap", unknown: "none" };
const CHECK_LABELS: Record<CheckState, string> = { pass: "Pass", warn: "Warning", fail: "Fail", unknown: "Unknown" };

const DOMAIN_FROM: Record<string, string> = { set: "set by you", website: "from the supplier's website", contact: "from the contact's email address" };

export const GUEST_FLAG_LABELS: Record<string, string> = {
  offboarded_supplier: "Supplier offboarded",
  dormant: "Dormant",
  no_mfa: "Signs in without MFA",
  unknown_domain: "Domain not on the register",
};

export const PARTY_SOURCE_LABELS: Record<string, string> = {
  declared: "Recorded by you",
  questionnaire: "Reported in a questionnaire",
  published_list: "Published sub-processor list",
  inferred: "Inferred from DNS",
};

/** The register's standing chip: signals, not a rating — never a number. */
export function StandingChip({ standing, concerns }: { standing: SupplierStanding; concerns?: number }) {
  const why = concerns ? `${concerns} signal${concerns === 1 ? "" : "s"} of concern. ` : "";
  return (
    <StatusTag tone={STANDING_TONE[standing]} title={`${why}Signals, not a rating: open the supplier's Assurance tab for each one.`}>
      {STANDING_LABELS[standing]}
    </StatusTag>
  );
}

function FlagChip({ flag }: { flag: string }) {
  const serious = flag === "offboarded_supplier" || flag === "no_mfa";
  return (
    <span style={{
      fontSize: 11, fontWeight: 600, padding: "1px 7px", borderRadius: 2, whiteSpace: "nowrap",
      background: serious ? theme.color.dangerBg : theme.color.warningBg, color: serious ? theme.color.dangerText : theme.color.warningText,
    }}>
      {GUEST_FLAG_LABELS[flag] ?? flag.replace(/_/g, " ")}
    </span>
  );
}

function Section({ title, hint, actions, children }: { title: string; hint?: React.ReactNode; actions?: React.ReactNode; children: React.ReactNode }) {
  return (
    <section style={{ marginBottom: 24 }}>
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <div style={{ ...text.sectionTitle, margin: "0 0 4px" }}>{title}</div>
        {actions}
      </div>
      {hint && <p style={{ ...text.hint, margin: "0 0 10px" }}>{hint}</p>}
      {children}
    </section>
  );
}

const th: React.CSSProperties = { padding: "8px 12px", textAlign: "left", fontWeight: 600, fontSize: 12, color: theme.color.muted, whiteSpace: "nowrap" };
const td: React.CSSProperties = { padding: "8px 12px", fontSize: 12, color: theme.color.text, verticalAlign: "top" };
const linkBtn: React.CSSProperties = { padding: 0, border: "none", background: "none", font: "inherit", color: "var(--c-accentText)", cursor: "pointer", textDecoration: "underline" };
const smallBtn: React.CSSProperties = { ...secondaryBtn, padding: "4px 10px", fontSize: 12 };

// ── Panel ─────────────────────────────────────────────────────────────────────

export function SupplierAssurancePanel({ supplierId, supplierName, canWrite }: { supplierId: string; supplierName: string; canWrite: boolean }) {
  const [view, setView] = useState<AssuranceView | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setView(await api.get<AssuranceView>(`/tprm/suppliers/${supplierId}/assurance`));
      setError(null);
    } catch (e) {
      setError(apiErrorMessage(e, "Could not load what is known about this supplier."));
    } finally {
      setLoading(false);
    }
  }, [supplierId]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={{ ...text.hint }}>Loading…</div>;
  if (!view) return <div role="alert" style={{ ...text.hint, color: theme.color.dangerText }}>{error ?? "Could not load what is known about this supplier."}</div>;

  const signals = [...view.signals].sort((a, b) => SIGNAL_RANK[a.state] - SIGNAL_RANK[b.state]);

  return (
    <div>
      {error && <div role="alert" style={{ ...text.hint, color: theme.color.dangerText, marginBottom: 12 }}>{error}</div>}

      {/* ── Standing and signals ── */}
      <Section title="Standing" hint="What the records and public sources say about this supplier, signal by signal. Signals, not a rating: nothing here is scored or added up.">
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
          <StatusTag tone={STANDING_TONE[view.standing]}>{STANDING_LABELS[view.standing]}</StatusTag>
          <span style={{ ...text.meta }}>Signals, not a rating</span>
        </div>
        {signals.length === 0 ? (
          <div style={{ ...emptyFrame, ...text.hint }}>Nothing is recorded yet that says how this supplier stands.</div>
        ) : (
          <div style={{ ...card, padding: 0 }}>
            {signals.map((s, i) => (
              <div key={s.key} style={{ display: "flex", gap: 12, alignItems: "flex-start", padding: "10px 12px", borderTop: i ? `1px solid ${theme.color.border}` : "none" }}>
                <div style={{ width: 92, flexShrink: 0 }}><StatusTag tone={SIGNAL_TONE[s.state]}>{SIGNAL_STATE_LABELS[s.state]}</StatusTag></div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ ...text.body, fontWeight: 600, color: theme.color.heading }}>{SIGNAL_LABELS[s.key] ?? s.key.replace(/_/g, " ")}</div>
                  <div style={{ ...text.hint }}>{s.detail}</div>
                  {s.source && <div style={{ ...text.meta }}>{s.source}</div>}
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      <SupplierAssuranceReports supplierId={supplierId} canWrite={canWrite} onChanged={load} />

      <SettingsRow supplierId={supplierId} view={view} canWrite={canWrite} onSaved={load} />

      {/* ── Public domain checks ── */}
      <Section title="Public domain checks" hint={<>No supplier system was tested; public records only. The platform reads the domain's DNS records and requests its public website, as any visitor would.</>}>
        {!view.domainCheck ? (
          <div style={{ ...emptyFrame, ...text.hint }}>
            {view.domain ? `No check of ${view.domain.domain} has run yet. Checks run weekly when switched on (Monitoring tab), or use Check now.` : "No domain is known for this supplier. Set one above, or add the supplier's website."}
          </div>
        ) : (
          <>
            <div style={{ ...text.hint, marginBottom: 8 }}>
              {view.domainCheck.result.domain}{view.domainCheck.result.host && view.domainCheck.result.host !== view.domainCheck.result.domain ? ` (website ${view.domainCheck.result.host})` : ""} · checked {fmtDate(view.domainCheck.checkedAt)}
              {view.domainCheck.result.certificateExpiresAt ? ` · website certificate expires ${fmtDate(view.domainCheck.result.certificateExpiresAt)}` : ""}
            </div>
            {view.domainCheck.current === false && (
              <div style={{ ...text.hint, color: theme.color.warningText, marginBottom: 8 }}>
                This check was of {view.domainCheck.result.domain}, which is no longer this supplier's domain, so it is not counted in the signals above.
                {view.domain ? ` The next check will look at ${view.domain.domain}.` : " Set the supplier's domain above to check it again."}
              </div>
            )}
            <div style={{ ...card, padding: 0, overflowX: "auto", marginBottom: 10 }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead><tr style={{ borderBottom: "1px solid var(--c-line)" }}><th style={th}>Check</th><th style={th}>Result</th><th style={th}>What it found</th></tr></thead>
                <tbody>
                  {DOMAIN_CHECKS.map((c, i) => {
                    const r = view.domainCheck!.result.checks[c.key];
                    return (
                      <tr key={c.key} style={{ borderTop: i ? `1px solid ${theme.color.border}` : "none" }}>
                        <td style={{ ...td, fontWeight: 600, whiteSpace: "nowrap" }}>{c.label}</td>
                        <td style={td}>{r ? <StatusTag tone={CHECK_TONE[r.state]}>{CHECK_LABELS[r.state]}</StatusTag> : <StatusTag tone="none">Not checked</StatusTag>}</td>
                        <td style={{ ...td, color: theme.color.muted }}>{r?.summary ?? "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {view.domainCheck.result.findings.length > 0 && (
              <div style={{ display: "grid", gap: 6, marginBottom: 8 }}>
                {view.domainCheck.result.findings.map((f, i) => (
                  <div key={`${f.check}-${i}`} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                    <SeverityPill value={f.severity} />
                    <div>
                      <div style={{ ...text.body, fontWeight: 600 }}>{f.title}</div>
                      <div style={{ ...text.hint }}>{f.detail}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {view.domainHistory.length > 1 && (
              <div style={{ ...text.meta }}>
                Recent checks:{view.domainHistory.slice(0, 6).map((h) => `${fmtDate(h.checkedAt)} (${h.findingCount ? `${h.findingCount} finding${h.findingCount === 1 ? "" : "s"}${h.worstSeverity ? `, worst ${h.worstSeverity}` : ""}` : "nothing found"})`).join(" · ")}
              </div>
            )}
          </>
        )}
      </Section>

      {/* ── Vendor rating ── */}
      <RatingSection ratings={view.ratings} />

      {/* ── Guest accounts ── */}
      <Section title="Guest access to your tenant" hint={`Guest accounts in your Microsoft 365 tenant that belong to ${supplierName}, read by the Microsoft 365 Security connector.`}>
        {view.guests.length === 0 ? (
          <div style={{ ...emptyFrame, ...text.hint }}>No guest account is matched to this supplier. Guest accounts are read when Microsoft 365 Security is connected with supplier guest accounts switched on.</div>
        ) : (
          <div style={{ ...card, padding: 0, overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr style={{ borderBottom: "1px solid var(--c-line)" }}><th style={th}>Account</th><th style={th}>Enabled</th><th style={th}>Last sign-in</th><th style={th}>MFA (30 days)</th><th style={th}>Flags</th></tr></thead>
              <tbody>
                {view.guests.map((g, i) => (
                  <tr key={g.id} style={{ borderTop: i ? `1px solid ${theme.color.border}` : "none" }}>
                    <td style={{ ...td, maxWidth: 220 }}>
                      <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.displayName || g.userPrincipalName}</div>
                      <div style={{ ...text.meta, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{g.userPrincipalName}</div>
                    </td>
                    <td style={td}>{g.accountEnabled ? "Yes" : "No"}</td>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>{g.lastSignInAt ? fmtDate(g.lastSignInAt) : "Never"}</td>
                    <td style={{ ...td, whiteSpace: "nowrap" }}>{g.recentSignIns ? `${g.recentMfaSignIns} of ${g.recentSignIns} sign-ins` : "No sign-ins"}</td>
                    <td style={td}><div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>{g.flags.length ? g.flags.map((f) => <FlagChip key={f} flag={f} />) : <span style={{ color: theme.color.muted }}>—</span>}</div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      {/* ── Fourth parties ── */}
      <FourthPartiesSection supplierId={supplierId} parties={view.fourthParties} watch={view.subProcessorWatch} canWrite={canWrite} onChanged={load} />
    </div>
  );
}

// ── Domain and published list settings, Check now ────────────────────────────

function SettingsRow({ supplierId, view, canWrite, onSaved }: { supplierId: string; view: AssuranceView; canWrite: boolean; onSaved: () => void }) {
  const [domain, setDomain] = useState(view.settings.domain ?? "");
  const [listUrl, setListUrl] = useState(view.settings.subProcessorListUrl ?? "");
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);

  const dirty = domain.trim() !== (view.settings.domain ?? "") || listUrl.trim() !== (view.settings.subProcessorListUrl ?? "");

  async function save() {
    setSaving(true);
    setMessage(null);
    try {
      const r = await api.put<{ domain: string | null; subProcessorListUrl: string | null }>(`/tprm/suppliers/${supplierId}/assurance-settings`, {
        domain: domain.trim() || null,
        subProcessorListUrl: listUrl.trim(),
      });
      setDomain(r.domain ?? "");
      setListUrl(r.subProcessorListUrl ?? "");
      setMessage({ ok: true, text: "Saved." });
      onSaved();
    } catch (e) {
      setMessage({ ok: false, text: apiErrorMessage(e, "Could not save.") });
    } finally {
      setSaving(false);
    }
  }

  async function checkNow() {
    setChecking(true);
    setMessage(null);
    try {
      await api.post(`/tprm/suppliers/${supplierId}/check-now`, {});
      setMessage({ ok: true, text: "Queued. The domain check, vendor rating and published list are read on the worker; reload in a minute or two to see the results." });
    } catch (e) {
      setMessage({ ok: false, text: apiErrorMessage(e, "The check could not be queued.") });
    } finally {
      setChecking(false);
    }
  }

  const derived = view.domain && view.domain.from !== "set" ? view.domain : null;

  return (
    <Section title="What is checked" hint="The supplier's public internet domain, and the page where it publishes its sub-processors (watched for changes).">
      {canWrite ? (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 10, alignItems: "end" }}>
          <label style={{ display: "block" }}>
            <span style={text.label}>Domain</span>
            <input type="text" value={domain} onChange={(e) => setDomain(e.target.value)} maxLength={253}
              placeholder={derived ? `${derived.domain} (${DOMAIN_FROM[derived.from]})` : "example.com"} style={inputStyle} />
          </label>
          <label style={{ display: "block" }}>
            <span style={text.label}>Published sub-processor page</span>
            <input type="url" value={listUrl} onChange={(e) => setListUrl(e.target.value)} placeholder="https://example.com/subprocessors" style={inputStyle} />
          </label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", gridColumn: "1 / -1" }}>
            <button type="button" onClick={save} disabled={!dirty || saving} style={{ ...btnPrimary, padding: "6px 14px", fontSize: 12, opacity: !dirty || saving ? 0.6 : 1 }}>{saving ? "Saving…" : "Save"}</button>
            <button type="button" onClick={checkNow} disabled={checking} style={{ ...smallBtn, padding: "6px 12px", opacity: checking ? 0.6 : 1 }}>{checking ? "Queuing…" : "Check now"}</button>
          </div>
        </div>
      ) : (
        <div style={{ ...text.hint }}>
          Domain: <strong style={{ color: theme.color.text }}>{view.domain ? `${view.domain.domain} (${DOMAIN_FROM[view.domain.from]})` : "none known"}</strong>
          {" · "}Published sub-processor page: {safeHref(view.settings.subProcessorListUrl) ? <a href={safeHref(view.settings.subProcessorListUrl)} target="_blank" rel="noopener noreferrer" style={{ color: "var(--c-accentText)" }}>{view.settings.subProcessorListUrl}</a> : "none recorded"}
        </div>
      )}
      {canWrite && derived && !domain.trim() && (
        <div style={{ ...text.meta, marginTop: 6 }}>Checked as {derived.domain}, {DOMAIN_FROM[derived.from]}. Set a domain to check another.</div>
      )}
      {message && <div role={message.ok ? "status" : "alert"} style={{ ...text.hint, marginTop: 8, color: message.ok ? theme.color.successText : theme.color.dangerText }}>{message.text}</div>}
    </Section>
  );
}

// ── Vendor rating ─────────────────────────────────────────────────────────────

function RatingSection({ ratings }: { ratings: Rating[] }) {
  const sorted = [...ratings].sort((a, b) => a.observedOn.localeCompare(b.observedOn));
  const cutoff = new Date(Date.now() - 90 * 86_400_000).toISOString().slice(0, 10);
  const recent = sorted.filter((r) => r.observedOn >= cutoff);
  const latest = sorted[sorted.length - 1];
  return (
    <Section title="Vendor rating" hint="From the organisation's own account with a ratings vendor (Monitoring tab), where one is connected. The vendor's view of the supplier, beside the records, never instead of them.">
      {!latest ? (
        <div style={{ ...emptyFrame, ...text.hint }}>No rating has been read for this supplier.</div>
      ) : (
        <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div>
            <div style={{ ...text.stat }}>{latest.score}<span style={{ ...text.hint }}> / {latest.scaleMax}</span>{latest.grade ? <span style={{ ...text.hint, marginLeft: 8 }}>grade {latest.grade}</span> : null}</div>
            <div style={{ ...text.meta }}>{latest.vendor} · {fmtDateOnly(latest.observedOn)}</div>
          </div>
          {recent.length > 1 && (
            <div aria-label={`Vendor rating over 90 days: ${recent.map((r) => r.score).join(", ")}`} role="img" style={{ display: "flex", alignItems: "flex-end", gap: 2, height: 36, borderBottom: "1px solid var(--c-line)", paddingBottom: 1 }}>
              {recent.map((r) => (
                <span key={r.observedOn} title={`${fmtDateOnly(r.observedOn)}: ${r.score} / ${r.scaleMax}${r.grade ? ` (${r.grade})` : ""}`}
                  style={{ width: 5, height: `${Math.max(6, Math.round((r.scaleMax > 0 ? r.score / r.scaleMax : 0) * 100))}%`, background: "var(--c-accent)", opacity: 0.85 }} />
              ))}
            </div>
          )}
          {recent.length > 1 && <div style={{ ...text.meta }}>last 90 days</div>}
        </div>
      )}
    </Section>
  );
}

// ── Fourth parties ────────────────────────────────────────────────────────────

interface PartyForm { name: string; service: string; country: string; lei: string; handlesPersonalData: "" | "yes" | "no"; notes: string }
const EMPTY_FORM: PartyForm = { name: "", service: "", country: "", lei: "", handlesPersonalData: "", notes: "" };

const isKnown = (p: FourthParty) => p.sources.includes("declared") || !!p.confirmedAt;
const isReported = (p: FourthParty) => !isKnown(p) && (p.sources.includes("questionnaire") || p.sources.includes("published_list"));

function formFor(p: FourthParty): PartyForm {
  return {
    name: p.name, service: p.service ?? "", country: p.country ?? "", lei: p.lei ?? "",
    handlesPersonalData: p.handlesPersonalData == null ? "" : p.handlesPersonalData ? "yes" : "no", notes: p.notes ?? "",
  };
}

function bodyFor(f: PartyForm) {
  return {
    name: f.name.trim(),
    service: f.service.trim(),
    country: f.country.trim() || null,
    lei: f.lei.trim() || null,
    handlesPersonalData: f.handlesPersonalData === "" ? null : f.handlesPersonalData === "yes",
    notes: f.notes.trim() || null,
  };
}

function FourthPartiesSection({ supplierId, parties, watch, canWrite, onChanged }: {
  supplierId: string;
  parties: FourthParty[];
  watch: AssuranceView["subProcessorWatch"];
  canWrite: boolean;
  onChanged: () => void;
}) {
  // null = no form open; "new" = adding; otherwise the id being edited.
  const [editing, setEditing] = useState<string | null>(null);
  const [form, setForm] = useState<PartyForm>(EMPTY_FORM);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showRemoved, setShowRemoved] = useState(false);

  const live = parties.filter((p) => !p.removedAt);
  const removed = parties.filter((p) => p.removedAt);
  const known = live.filter(isKnown);
  const reported = live.filter(isReported);
  const inferred = live.filter((p) => !isKnown(p) && !isReported(p));

  function open(p: FourthParty | null) {
    setError(null);
    setEditing(p ? p.id : "new");
    setForm(p ? formFor(p) : EMPTY_FORM);
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name.trim()) { setError("Give the subcontractor's name."); return; }
    setBusy("form");
    setError(null);
    try {
      if (editing === "new") await api.post(`/tprm/suppliers/${supplierId}/subcontractors`, bodyFor(form));
      else await api.patch(`/tprm/subcontractors/${editing}`, bodyFor(form));
      setEditing(null);
      onChanged();
    } catch (err) {
      setError(apiErrorMessage(err, "Could not save."));
    } finally {
      setBusy(null);
    }
  }

  async function confirmParty(p: FourthParty) {
    setBusy(p.id);
    setError(null);
    try {
      await api.patch(`/tprm/subcontractors/${p.id}`, { confirm: true });
      onChanged();
    } catch (err) {
      setError(apiErrorMessage(err, "Could not confirm."));
    } finally {
      setBusy(null);
    }
  }

  async function remove(p: FourthParty) {
    if (!confirm(`Remove ${p.name} from this supplier's subcontractors?`)) return;
    setBusy(p.id);
    setError(null);
    try {
      await api.del(`/tprm/subcontractors/${p.id}`);
      onChanged();
    } catch (err) {
      setError(apiErrorMessage(err, "Could not remove."));
    } finally {
      setBusy(null);
    }
  }

  function row(p: FourthParty, i: number, extra?: React.ReactNode) {
    const declaredOnly = p.sources.length > 0 && p.sources.every((s) => s === "declared");
    return (
      <div key={p.id} style={{ padding: "10px 12px", borderTop: i ? `1px solid ${theme.color.border}` : "none" }}>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap" }}>
          <div style={{ minWidth: 0, flex: "1 1 260px" }}>
            <div style={{ ...text.body, fontWeight: 600, color: theme.color.heading }}>
              {p.name}
              {p.linkedSupplier && <span style={{ ...text.meta, marginLeft: 6 }}>also your supplier</span>}
            </div>
            <div style={{ ...text.hint }}>
              {[p.service, p.country, p.lei ? `LEI ${p.lei}` : null, p.handlesPersonalData == null ? null : p.handlesPersonalData ? "handles personal data" : "no personal data"].filter(Boolean).join(" · ") || "No detail recorded"}
            </div>
            <div style={{ ...text.meta }}>
              {p.sources.map((s) => PARTY_SOURCE_LABELS[s] ?? s).join(", ")}
              {p.confirmedAt ? ` · confirmed ${fmtDate(p.confirmedAt)}` : ""}
              {p.lastSeenAt && !p.removedAt ? ` · last seen ${fmtDate(p.lastSeenAt)}` : ""}
              {p.removedAt ? ` · no longer reported since ${fmtDate(p.removedAt)}` : ""}
            </div>
            {extra}
            {p.notes && <div style={{ ...text.hint, marginTop: 2 }}>{p.notes}</div>}
          </div>
          {canWrite && !p.removedAt && (
            <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
              {!p.confirmedAt && <button type="button" onClick={() => confirmParty(p)} disabled={busy === p.id} style={{ ...btnPrimary, padding: "4px 10px", fontSize: 12, opacity: busy === p.id ? 0.6 : 1 }}>Confirm</button>}
              <button type="button" onClick={() => open(p)} style={smallBtn}>Edit</button>
              {declaredOnly && <button type="button" onClick={() => remove(p)} disabled={busy === p.id} style={{ ...dangerBtn, padding: "4px 10px", fontSize: 12 }}>Delete</button>}
            </div>
          )}
        </div>
      </div>
    );
  }

  const group = (title: string, hint: string, list: FourthParty[], extra?: (p: FourthParty) => React.ReactNode) => list.length > 0 && (
    <div style={{ marginBottom: 12 }}>
      <div style={{ ...text.label, marginBottom: 2 }}>{title} <span style={{ ...text.meta }}>{list.length}</span></div>
      <div style={{ ...text.meta, marginBottom: 6 }}>{hint}</div>
      <div style={{ ...card, padding: 0 }}>{list.map((p, i) => row(p, i, extra?.(p)))}</div>
    </div>
  );

  return (
    <Section
      title="Its own suppliers"
      hint="The supplier's subcontractors and sub-processors (fourth parties): those you record, those it reports in a questionnaire or publishes, and those its public DNS points to."
      actions={canWrite && editing === null ? <button type="button" onClick={() => open(null)} style={smallBtn}>Add subcontractor</button> : undefined}
    >
      {watch?.url && (
        <div style={{ ...text.hint, marginBottom: 10 }}>
          Published list {safeHref(watch.url) ? <a href={safeHref(watch.url)} target="_blank" rel="noopener noreferrer" style={{ color: "var(--c-accentText)" }}>{watch.url}</a> : watch.url}
          {watch.checkedAt ? ` · read ${fmtDate(watch.checkedAt)}` : " · not read yet"}
          {watch.changedAt ? ` · last changed ${fmtDate(watch.changedAt)}` : ""}
          {watch.error && <div style={{ color: theme.color.warningText }}>{watch.error}</div>}
        </div>
      )}

      {error && <div role="alert" style={{ ...text.hint, color: theme.color.dangerText, marginBottom: 8 }}>{error}</div>}

      {editing !== null && (
        <form onSubmit={submit} style={{ ...card, padding: 14, marginBottom: 12, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10 }}>
          <div style={{ ...text.label, gridColumn: "1 / -1", margin: 0, color: theme.color.heading }}>{editing === "new" ? "Add a subcontractor" : "Edit subcontractor"}</div>
          <label><span style={text.label}>Name *</span><input type="text" value={form.name} required maxLength={200} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} style={inputStyle} /></label>
          <label><span style={text.label}>Service</span><input type="text" value={form.service} maxLength={300} onChange={(e) => setForm((f) => ({ ...f, service: e.target.value }))} placeholder="e.g. Hosting" style={inputStyle} /></label>
          <label><span style={text.label}>Country</span><input type="text" value={form.country} maxLength={100} onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))} style={inputStyle} /></label>
          <label><span style={text.label}>LEI</span><input type="text" value={form.lei} maxLength={20} onChange={(e) => setForm((f) => ({ ...f, lei: e.target.value }))} style={{ ...inputStyle, ...text.ref }} /></label>
          <label>
            <span style={text.label}>Handles personal data</span>
            <select value={form.handlesPersonalData} onChange={(e) => setForm((f) => ({ ...f, handlesPersonalData: e.target.value as PartyForm["handlesPersonalData"] }))} style={inputStyle}>
              <option value="">Not known</option>
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </label>
          <label style={{ gridColumn: "1 / -1" }}><span style={text.label}>Notes</span><textarea value={form.notes} maxLength={2000} rows={2} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} style={{ ...inputStyle, resize: "vertical" }} /></label>
          <div style={{ display: "flex", gap: 8, gridColumn: "1 / -1" }}>
            <button type="submit" disabled={busy === "form"} style={{ ...btnPrimary, padding: "6px 14px", fontSize: 12, opacity: busy === "form" ? 0.6 : 1 }}>{busy === "form" ? "Saving…" : "Save"}</button>
            <button type="button" onClick={() => { setEditing(null); setError(null); }} style={smallBtn}>Cancel</button>
          </div>
        </form>
      )}

      {live.length === 0 && editing === null && (
        <div style={{ ...emptyFrame, ...text.hint }}>No subcontractor is recorded for this supplier.</div>
      )}

      {group("Known", "Recorded by you, or confirmed after review.", known)}
      {group("Reported, awaiting review", "From a returned questionnaire or the supplier's published list. Confirm each once you have reviewed it.", reported)}
      {group("Inferred from DNS", "Services the supplier's public DNS records point to, such as its mail or hosting provider. Unconfirmed until reviewed.", inferred,
        (p) => p.inferredFrom ? <div style={{ ...text.meta }}>Seen in {p.inferredFrom}</div> : null)}

      {removed.length > 0 && (
        <div>
          <button type="button" onClick={() => setShowRemoved((v) => !v)} style={{ ...linkBtn, ...text.hint, color: "var(--c-accentText)" }}>
            {showRemoved ? "Hide" : "Show"} {removed.length} no longer reported
          </button>
          {showRemoved && <div style={{ ...card, padding: 0, marginTop: 6 }}>{removed.map((p, i) => row(p, i))}</div>}
        </div>
      )}
    </Section>
  );
}
